import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, X, Check, Checks } from "@phosphor-icons/react";

export default function AdminTickets() {
  const [tickets, setTickets] = useState([]);
  const [status, setStatus] = useState("");
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");

  const load = async () => {
    const q = status ? `?status=${status}` : "";
    const { data } = await api.get(`/admin/tickets${q}`);
    setTickets(data);
  };
  useEffect(() => { load(); }, [status]);

  const open = async (t) => { setActive(t); const { data } = await api.get(`/tickets/${t.id}`); setDetail(data); };
  const sendReply = async () => {
    if (!reply.trim()) return;
    await api.post(`/tickets/${active.id}/reply`, { body: reply });
    setReply(""); open(active);
  };
  const close = async () => { await api.post(`/tickets/${active.id}/close`); toast.success("Closed"); setActive(null); setDetail(null); load(); };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Support Tickets</h1>
        <div className="flex gap-1 rounded-full bg-slate-100 dark:bg-slate-800 p-1">
          {[["", "All"], ["open", "Open"], ["in_progress", "In progress"], ["closed", "Closed"]].map(([v, l]) => (
            <button key={v} data-testid={`admin-tickets-tab-${v || "all"}`} onClick={() => setStatus(v)} className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors duration-200 ${status === v ? "bg-blue-700 text-white" : "text-slate-600 dark:text-slate-300"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-1 space-y-2">
          {tickets.length === 0 && <div className="text-slate-500 text-sm">No tickets.</div>}
          {tickets.map((t) => (
            <button key={t.id} data-testid={`admin-ticket-${t.id}`} onClick={() => open(t)} className={`w-full text-left rounded-2xl border p-3 transition-colors duration-200 ${active?.id === t.id ? "border-blue-600 bg-blue-50 dark:bg-blue-950/40" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-300"}`}>
              <div className="font-bold text-slate-900 dark:text-white text-sm">{t.subject}</div>
              <div className="text-xs text-slate-500">@{t.username} • {t.status}</div>
            </button>
          ))}
        </div>
        <div className="md:col-span-2">
          {active && detail && (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col" style={{ minHeight: 500 }}>
              <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
                <div>
                  <div className="font-black text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{detail.ticket.subject}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-widest">@{detail.ticket.username} • {detail.ticket.status}</div>
                  {detail.ticket.last_seen_by_user_at && (
                    <div data-testid="ticket-last-seen" className="text-[10px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400 mt-1 inline-flex items-center gap-1">
                      <Checks size={11} weight="bold" /> User last viewed · {new Date(detail.ticket.last_seen_by_user_at).toLocaleString()}
                    </div>
                  )}
                </div>
                {detail.ticket.status !== "closed" && <button data-testid="admin-close-ticket-btn" onClick={close} className="rounded-full bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300 font-bold px-3 py-1.5 text-sm inline-flex items-center gap-1"><X size={14} weight="bold" /> Close</button>}
              </div>
              <div className="flex-1 p-4 space-y-3 overflow-y-auto">
                {detail.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.is_admin ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-md ${m.is_admin ? "text-right" : "text-left"}`}>
                      <div className={`rounded-2xl px-4 py-2 text-sm ${m.is_admin ? "bg-blue-600 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100"}`}>
                        <div className="text-xs font-bold uppercase tracking-widest opacity-70">{m.is_admin ? "Admin" : "User"}</div>
                        <div className="whitespace-pre-wrap">{m.body}</div>
                      </div>
                      {m.is_admin && (
                        <div
                          data-testid={`ticket-msg-${m.id}-seen`}
                          className={`mt-1 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest ${
                            m.seen_by_user_at
                              ? "text-emerald-600 dark:text-emerald-400"
                              : "text-slate-400 dark:text-slate-500"
                          }`}
                        >
                          {m.seen_by_user_at ? (
                            <>
                              <Checks size={12} weight="bold" /> Seen · {new Date(m.seen_by_user_at).toLocaleString()}
                            </>
                          ) : (
                            <>
                              <Check size={12} weight="bold" /> Sent
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {detail.ticket.status !== "closed" && (
                <div className="p-3 border-t border-slate-200 dark:border-slate-800 flex gap-2">
                  <input data-testid="admin-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply to user…" className="flex-1 rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-4 py-2 text-slate-900 dark:text-slate-100" />
                  <button data-testid="admin-send-btn" onClick={sendReply} className="rounded-full bg-blue-700 hover:bg-blue-800 text-white font-bold px-4 py-2 inline-flex items-center gap-1 transition-colors duration-200"><PaperPlaneTilt size={16} weight="fill" /> Send</button>
                </div>
              )}
            </div>
          )}
          {!active && <div className="rounded-2xl border-2 border-dashed p-10 text-center text-slate-500">Select a ticket.</div>}
        </div>
      </div>
    </AdminLayout>
  );
}
